import React, { useRef, useState } from "react";
import { authFetch, API } from "../context/AuthContext";
import axios from "axios";
import { Plus, X, Camera, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_PHOTOS = 5;

// File URL on the backend (auth required)
export function fileUrl(storagePath) {
    return `${API}/files/${storagePath}`;
}

export default function PhotoGallery({ photos = [], onChange, verified = false, onVerify }) {
    const inputRef = useRef();
    const selfieRef = useRef();
    const [uploading, setUploading] = useState(false);
    const [verifying, setVerifying] = useState(false);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.post(`${API}/profile/photos`, fd, {
                headers: { Authorization: token ? `Bearer ${token}` : undefined },
                withCredentials: true,
            });
            onChange?.(res.data.photos);
            toast.success("Photo added to your gallery.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const remove = async (photoId) => {
        try {
            const res = await authFetch("delete", `/profile/photos/${photoId}`);
            onChange?.(res.data.photos);
            toast.success("Photo removed.");
        } catch {
            toast.error("Failed to remove");
        }
    };

    const submitSelfie = async (file) => {
        if (!file) return;
        if (photos.length === 0) {
            toast.error("Add at least one profile photo first.");
            return;
        }
        setVerifying(true);
        const fd = new FormData();
        fd.append("selfie", file);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.post(`${API}/profile/verify-selfie`, fd, {
                headers: { Authorization: token ? `Bearer ${token}` : undefined },
                withCredentials: true,
            });
            if (res.data.verified) {
                toast.success(`Verified! (confidence ${res.data.confidence}%)`);
                onVerify?.(true);
            } else {
                toast.error(`Could not verify: ${res.data.reason} (confidence ${res.data.confidence}%)`);
                onVerify?.(false);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Verification failed");
        } finally {
            setVerifying(false);
        }
    };

    return (
        <div className="glass p-6 space-y-5">
            <div className="flex items-baseline justify-between">
                <div>
                    <p className="label-tracking text-gold">Photos</p>
                    <h3 className="font-serif text-2xl mt-1">Your Gallery</h3>
                </div>
                <p className="text-xs text-muted">{photos.length} / {MAX_PHOTOS}</p>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {photos.map((p) => (
                    <div key={p.photo_id} className="relative aspect-square rounded-xl overflow-hidden border border-white/10 group" data-testid={`photo-${p.photo_id}`}>
                        <img src={fileUrl(p.storage_path)} alt="" className="w-full h-full object-cover" />
                        <button
                            onClick={() => remove(p.photo_id)}
                            className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Remove photo"
                            data-testid={`remove-photo-${p.photo_id}`}
                        >
                            <X size={14} className="text-white" />
                        </button>
                    </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                    <button
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        className="aspect-square rounded-xl border border-dashed border-white/20 hover:border-gold/50 hover:bg-gold/5 transition-colors flex flex-col items-center justify-center gap-1 text-muted hover:text-gold"
                        data-testid="upload-photo-btn"
                    >
                        <Plus size={24} />
                        <span className="text-[10px] label-tracking">{uploading ? "Uploading" : "Add"}</span>
                    </button>
                )}
            </div>
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
                data-testid="photo-file-input"
            />

            <div className="border-t border-white/10 pt-4">
                <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck size={18} weight="duotone" className={verified ? "text-gold" : "text-muted"} />
                    <p className="label-tracking text-gold">Identity</p>
                    {verified && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gold/15 text-gold border border-gold/30">Verified ✦</span>
                    )}
                </div>
                <p className="text-xs text-muted leading-relaxed">
                    Take a live selfie — we'll use AI to confirm it matches your first photo. Verified members
                    are shown a "Verified ✦" badge in matches and search.
                </p>
                <button
                    onClick={() => selfieRef.current?.click()}
                    disabled={verifying || photos.length === 0}
                    className="btn-gold mt-3 inline-flex items-center gap-2"
                    data-testid="verify-selfie-btn"
                >
                    <Camera size={16} /> {verifying ? "Verifying..." : (verified ? "Re-verify" : "Verify Me")}
                </button>
                <input
                    ref={selfieRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    capture="user"
                    className="hidden"
                    onChange={(e) => submitSelfie(e.target.files?.[0])}
                    data-testid="selfie-file-input"
                />
            </div>
        </div>
    );
}
